import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import SellerLayout from "@/components/SellerLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User as UserIcon, Building2, Mail, Phone, Calendar, CheckCircle, XCircle, ArrowLeft, Key } from "lucide-react";
import { formatDateIST } from "@/utils/dateUtils";

const ROLE_COLORS = {
  admin: "bg-purple-100 text-purple-800",
  admin_user: "bg-purple-100 text-purple-800",
  account_manager: "bg-indigo-100 text-indigo-800",
  seller_admin: "bg-blue-100 text-blue-800",
  seller_user: "bg-cyan-100 text-cyan-800"
};

const ROLE_LABELS = {
  admin: "Admin",
  admin_user: "Admin User",
  account_manager: "Account Manager",
  seller_admin: "Seller Admin",
  seller_user: "Seller User"
};

// Platform principals get the admin portal chrome; everyone else is seller-side.
const PLATFORM_ROLES = ["admin", "admin_user", "account_manager"];

const ProfilePage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [resetting, setResetting] = useState(false);
  const isPlatformUser = PLATFORM_ROLES.includes(user.role);
  const Layout = isPlatformUser ? AdminLayout : SellerLayout;

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      // Own identity only: /auth/me is ungated, unlike /admin/profile/{id}
      // (users:view) — this page is also the landing for permissionless users.
      const endpoint = isPlatformUser ? `/auth/me` : `/seller/profile`;
      const response = await axios.get(`${API}${endpoint}`);
      setProfileData(response.data);
    } catch (error) {
      toast.error("Failed to fetch profile");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!passwords.currentPassword) {
      toast.error("Enter your current password");
      return;
    }
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (passwords.newPassword.length < 8 || !/[a-zA-Z]/.test(passwords.newPassword) || !/\d/.test(passwords.newPassword)) {
      toast.error("Password must be at least 8 characters and include a letter and a number");
      return;
    }

    setResetting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        user_id: user.id,
        current_password: passwords.currentPassword,
        new_password: passwords.newPassword
      });
      toast.success("Password updated successfully!");
      setShowPasswordDialog(false);
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex justify-center py-12">
          <div className="text-slate-600">Loading profile...</div>
        </div>
      </Layout>
    );
  }

  if (!profileData) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="text-center py-12 text-slate-600">Profile not found</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-4xl font-bold text-slate-800">My Profile</h1>
              <p className="text-slate-600">View your account information</p>
            </div>
          </div>
          <Button 
            onClick={() => setShowPasswordDialog(true)}
            style={{backgroundColor: '#00A699'}}
          >
            <Key className="w-4 h-4 mr-2" />
            Reset Password
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Profile Card */}
          <Card className="border-0 shadow-lg md:col-span-1">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-24 h-24 rounded-full flex items-center justify-center" style={{backgroundColor: '#E6F7F5'}}>
                  <UserIcon className="w-12 h-12" style={{color: '#00A699'}} />
                </div>
              </div>
              <CardTitle className="text-xl">{profileData.name}</CardTitle>
              <Badge className={`${ROLE_COLORS[profileData.role] || 'bg-gray-100 text-gray-800'} border-0 mt-2`}>
                {ROLE_LABELS[profileData.role] || profileData.role}
              </Badge>
            </CardHeader>
            <CardContent className="text-center">
              {profileData.is_active ? (
                <div className="flex items-center justify-center text-green-600">
                  <CheckCircle className="w-5 h-5 mr-2" />
                  <span className="font-semibold">Active</span>
                </div>
              ) : (
                <div className="flex items-center justify-center text-red-600">
                  <XCircle className="w-5 h-5 mr-2" />
                  <span className="font-semibold">Inactive</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Details Card */}
          <Card className="border-0 shadow-lg md:col-span-2">
            <CardHeader>
              <CardTitle>Account Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center text-slate-600">
                    <Mail className="w-4 h-4 mr-2" />
                    <span className="text-sm font-semibold">Email</span>
                  </div>
                  <p className="text-slate-800 ml-6">{profileData.email}</p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center text-slate-600">
                    <Phone className="w-4 h-4 mr-2" />
                    <span className="text-sm font-semibold">Phone</span>
                  </div>
                  <p className="text-slate-800 ml-6">{profileData.phone || 'Not provided'}</p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center text-slate-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span className="text-sm font-semibold">Member Since</span>
                  </div>
                  <p className="text-slate-800 ml-6">
                    {formatDateIST(profileData.created_at, 'long')}
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center text-slate-600">
                    <UserIcon className="w-4 h-4 mr-2" />
                    <span className="text-sm font-semibold">Role</span>
                  </div>
                  <Badge className={`ml-6 ${ROLE_COLORS[profileData.role] || 'bg-gray-100 text-gray-800'} border-0`}>
                    {ROLE_LABELS[profileData.role] || profileData.role}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Seller Information */}
        {profileData.seller_info && (
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <div className="flex items-center">
                <Building2 className="w-5 h-5 mr-2" style={{color: '#00A699'}} />
                <CardTitle>Organization Details</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-slate-600">Company Name</p>
                  <p className="text-slate-800">{profileData.seller_info.company_name}</p>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-semibold text-slate-600">Seller ID</p>
                  <p className="text-slate-800 font-mono text-sm">{profileData.seller_info.seller_id}</p>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-semibold text-slate-600">GST Number</p>
                  <p className="text-slate-800">{profileData.seller_info.gst_number}</p>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-semibold text-slate-600">Mobile Number</p>
                  <p className="text-slate-800">{profileData.seller_info.mobile_number}</p>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <p className="text-sm font-semibold text-slate-600">Registered Address</p>
                  <p className="text-slate-800">
                    {profileData.seller_info.registered_address}, {profileData.seller_info.city}, {profileData.seller_info.state} - {profileData.seller_info.pincode}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Password Reset Dialog */}
        <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                Enter your new password below
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password *</Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={passwords.currentPassword}
                  onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                  placeholder="Enter current password"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password *</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={passwords.newPassword}
                  onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
                  placeholder="At least 8 chars, a letter and a number"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={passwords.confirmPassword}
                  onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handlePasswordReset} 
                  disabled={resetting}
                  style={{backgroundColor: '#00A699'}}
                >
                  {resetting ? "Resetting..." : "Reset Password"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ProfilePage;
